/**
 * Captions Library — public archive of past captioning sessions for an
 * event.  Lets a delegate (or anyone with the event id) re-read sessions
 * they attended in their preferred language, search a transcript by
 * keyword, and jump to a specific moment.
 *
 * Route: /event/:eventId/accessibility/library
 *
 * No auth required (matches the live delegate portal — captions are public
 * once a session ends, just like sermon transcripts on most church sites).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Calendar, ArrowLeft, Languages, Download, FileText, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '';

const CaptionsLibrary = () => {
  const { eventId } = useParams();
  const [params, setParams] = useSearchParams();
  const initialLang = params.get('lang') || (() => { try { return localStorage.getItem('acc:lang') || 'en'; } catch { return 'en'; }})();
  const [lang, setLang] = useState(initialLang);
  const [catalog, setCatalog] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [eventName, setEventName] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(params.get('session'));
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [busy, setBusy] = useState(false);

  // ---- Load languages + library ----
  useEffect(() => {
    fetch(`${API}/api/accessibility/languages`)
      .then(r => r.json()).then(d => setCatalog(d.languages || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/accessibility/events/${eventId}/library?lang=${lang}&limit=100`);
        const d = await r.json();
        if (!alive) return;
        setEventName(d.event_name || '');
        setSessions(d.sessions || []);
        if (!selectedId && d.sessions?.length) setSelectedId(d.sessions[0].id);
      } catch (_e) { /* network blip */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, [eventId, lang, selectedId]);

  // ---- Load full segments for selected session ----
  useEffect(() => {
    if (!selectedId) { setSelectedSegments([]); return; }
    setSearchResults(null);
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/accessibility/sessions/${selectedId}/segments?lang=${lang}&limit=500`);
        const d = await r.json();
        setSelectedSegments(d.segments || []);
      } catch (_e) {
        setSelectedSegments([]);
      }
    };
    load();
  }, [selectedId, lang]);

  // ---- Search within selected session ----
  const runSearch = async () => {
    if (!selectedId || !search.trim()) { setSearchResults(null); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/accessibility/sessions/${selectedId}/search?q=${encodeURIComponent(search)}&lang=${lang}`);
      const d = await r.json();
      setSearchResults(d.hits || []);
    } catch (_e) {
      setSearchResults([]);
    }
    setBusy(false);
  };

  const onPickSession = (id) => {
    setSelectedId(id);
    setSearchResults(null);
    setSearch('');
    setParams((p) => { p.set('session', id); p.set('lang', lang); return p; });
  };

  const selected = useMemo(() => sessions.find(s => s.id === selectedId), [sessions, selectedId]);
  const visibleSegments = searchResults || selectedSegments;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="captions-library">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Button variant="ghost" size="sm" onClick={() => window.history.back()} className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="h-6 w-6" /> {eventName || 'Captions library'}
            </h1>
            <p className="text-sm text-slate-500">Browse and search past captioning sessions.</p>
          </div>
          <div className="flex items-center gap-2">
            <Languages className="h-4 w-4 text-slate-400" />
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="w-44" data-testid="captions-library-lang">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {catalog.map(l => (
                  <SelectItem key={l.code} value={l.code} data-testid={`captions-library-lang-${l.code}`}>
                    {l.native} — {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
          {/* Sessions list */}
          <Card className="bg-white max-h-[80vh] overflow-y-auto">
            <CardHeader className="sticky top-0 bg-white border-b z-10">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" /> Sessions ({sessions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-1">
              {loading ? (
                <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : sessions.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">No archived sessions yet.</p>
              ) : sessions.map(s => (
                <button
                  key={s.id}
                  onClick={() => onPickSession(s.id)}
                  className={`w-full text-left p-2 rounded-md text-sm transition-colors ${
                    s.id === selectedId ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-slate-100'
                  }`}
                  data-testid={`captions-library-session-${s.id}`}
                >
                  <p className="font-medium truncate">{s.label}</p>
                  <p className="text-xs text-slate-500">{fmtDate(s.started_at)}{s.duration_minutes ? ` · ${s.duration_minutes}m` : ''}</p>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{s.preview || '—'}</p>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{s.segment_count} segs</Badge>
                    {(s.languages || []).slice(0, 3).map(lc => (
                      <Badge key={lc} variant="outline" className="text-[10px]">{lc}</Badge>
                    ))}
                    {(s.languages || []).length > 3 && <Badge variant="outline" className="text-[10px]">+{s.languages.length - 3}</Badge>}
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Transcript viewer */}
          <Card className="bg-white max-h-[80vh] flex flex-col">
            <CardHeader className="border-b">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <span className="truncate">{selected?.label || 'Pick a session'}</span>
                {selected && (
                  <div className="flex items-center gap-1">
                    {['txt', 'srt', 'vtt'].map(fmt => (
                      <Button key={fmt} size="sm" variant="ghost" asChild>
                        <a href={`${API}/api/accessibility/sessions/${selected.id}/export.${fmt}?lang=${lang}`}
                           target="_blank" rel="noreferrer"
                           data-testid={`captions-library-export-${fmt}`}>
                          <Download className="h-3 w-3 mr-1" />.{fmt}
                        </a>
                      </Button>
                    ))}
                  </div>
                )}
              </CardTitle>
              {selected && (
                <div className="flex items-center gap-2 pt-2">
                  <Search className="h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search within this session…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                    className="text-sm h-8"
                    data-testid="captions-library-search"
                  />
                  <Button size="sm" onClick={runSearch} disabled={busy || !search.trim()} data-testid="captions-library-search-btn">
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Find'}
                  </Button>
                  {searchResults && (
                    <Button size="sm" variant="ghost" onClick={() => { setSearchResults(null); setSearch(''); }}>
                      Clear
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-4">
              {!selected ? (
                <p className="text-sm text-slate-400 text-center py-12">Select a session on the left.</p>
              ) : visibleSegments.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-12">
                  {searchResults ? 'No matches.' : 'No transcript captured.'}
                </p>
              ) : (
                <div className="space-y-2" data-testid="captions-library-transcript">
                  {searchResults && (
                    <p className="text-xs text-slate-500">{searchResults.length} matches</p>
                  )}
                  {visibleSegments.map(s => (
                    <div key={s.id} className="flex gap-3" data-testid={`captions-library-segment-${s.id}`}>
                      <span className="text-[10px] text-slate-400 shrink-0 w-20 pt-1">
                        {s.ts ? new Date(s.ts).toLocaleTimeString() : ''}
                      </span>
                      <p className="text-sm leading-relaxed">{s.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CaptionsLibrary;
